import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { V_URL_API } from "../../../../../../utils/config";
import Fuse from "fuse.js";

/**
====================================================================
                       SECCIONES CURSOS COMPONENT
====================================================================
Componente que representa las secciones de cursos del alumno.
- Permite cambiar entre diferentes secciones de cursos.
- Muestra los cursos disponibles en la sección seleccionada.
- Permite seleccionar un curso específico.
====================================================================
*/

/**
 * Componente que representa las secciones de cursos del alumno.
 * @param {Array} vCursosTodos - Lista de todos los cursos.
 * @param {Object} vCursos - Objeto que contiene los cursos categorizados por estado.
 */
export default function SeccionesCursos({
  vCursosTodos = [],
  vCursos = {},
  vSeccionSeleccionada = "Todos",
  filterText = "", // Recibe el filterText
}) {
  const navigate = useNavigate();
  /**
   * Maneja la navegación a la sección de cursos seleccionada.
   * @param {string} seccion - La sección a navegar.
   */

  /**
   * Renderiza los cursos de una sección específica.
   * @param {Array} cursos - Lista de cursos a renderizar.
   * @returns {JSX.Element} - Elementos JSX que representan los cursos.
   */
  const mRenderCursos = (cursos) => {
    // Aseguramos que filterText sea una cadena (para evitar errores con trim())
    const searchTerm = (filterText || "").trim();

    const baseList = cursos || [];

    // Aplicar búsqueda difusa solo si hay texto en el input
    let filteredCourses = baseList;
    if (searchTerm !== "") {
      const fuse = new Fuse(baseList, {
        keys: ["nombre_curso"],
        threshold: 0.3,
      });
      const result = fuse.search(searchTerm);
      filteredCourses = result.map((r) => r.item);
    }

    cursos = filteredCourses;

    if (cursos.length === 0) {
      return (
        <div data-figma-id="2469:46068" class="alerta-no-encontrar">
          <p
            data-figma-id="2469:45979"
            class="alerta-no-encontrar-cursos-texto-1 "
          >
            Lo sentimos, no hemos encontrado resultados para “Curso {searchTerm}”
          </p>
          <div data-figma-id="2469:45980" class="frame-290009">
            <img
              class="file-1"
              src="../images/file-1.svg"
              data-figma-image-id="67f5c2d60b3f2b1fa6a0ffe5"
              alt=""
              data-figma-id="2469:46028"
              data-figma-image-hash="c657b7acce0455d1878dee5ea8f79879"
              loading="lazy"
            />
            <div
              data-figma-id="2469:45982"
              class="alerta-no-encontrar-cursos-contenido"
            >
              <p
                data-figma-id="2469:45983"
                class="alerta-no-encontrar-cursos-contenido-texto"
              >
                No existe en esta Plataforma, modifica tu búsqueda o prueba lo
                siguiente:
              </p>
              <div
                data-figma-id="2469:45984"
                class="alerta-no-encontrar-cursos-contenido-info"
              >
                <div
                  data-figma-id="2469:45985"
                  class="alerta-no-encontrar-cursos-info-1"
                >
                  <div data-figma-id="2469:46064" class="circulito-gris-claro">
                    <img
                      src="../images/Vector_3.svg"
                      data-figma-image-id="67f5c2d8405281f91e4dba11"
                      alt=""
                      data-figma-id="2469:46065"
                      data-figma-image-hash="cb50c9aa61f5c3c0ef583559b56541b2"
                      loading="lazy"
                    />
                  </div>
                  <p
                    data-figma-id="2469:45987"
                    class="alerta-no-encontrar-cursos-info-1-texto"
                  >
                    Asegúrate de que todas las palabras están escritas
                    correctamente
                  </p>
                </div>
                <div
                  data-figma-id="2469:45988"
                  class="alerta-no-encontrar-cursos-info-2"
                >
                  <div data-figma-id="2469:46066" class="circulito-gris-claro">
                    <img
                      src="../images/Vector_3.svg"
                      data-figma-image-id="67f5c2d8405281f91e4dba11"
                      alt=""
                      data-figma-id="2469:46067"
                      data-figma-image-hash="cb50c9aa61f5c3c0ef583559b56541b2"
                      loading="lazy"
                    />
                  </div>
                  <p
                    data-figma-id="2469:45990"
                    class="alerta-no-encontrar-cursos-info-2-texto"
                  >
                    Prueba con términos de búsqueda diferente
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return cursos.map((curso, index) => (
      <div
        class="tarjeta-mis-cursos"
        key={index}
        onClick={() => {
          if (curso.ultima_leccion.leccion_id) {
            navigate(
              `?f=cc&curso_id=${curso.curso_id}&leccion_id=${curso.ultima_leccion.leccion_id}`
            );
          } else {
            navigate(`?f=mc&curso_id=${curso.curso_id}`);
          }
        }}
      >
        <div class="tarjeta-mis-cursos-info-2-texto-1-movil">
          {curso.nombre_curso}
        </div>
        <div class="tarjeta-mis-cursos-info-1">
          <div
            class="imagen-curso"
            style={{
              backgroundImage: `url('${V_URL_API}/api/img_video_leccion/${
                curso.ultima_leccion.leccion_id
              }/thumbnail')`,
            }}
          >
            <div data-figma-id="1621:15241" class="icono-play-cards">
              <i className={`fi fi-sr-play tocar-1-card`}></i>
            </div>
          </div>
          <div class="barra-progreso-2">
            <div class="barra-progreso-duracion">
              <div class="barra-progreso-2-2"></div>
              <div
                class="barra-progreso-1-4"
                style={{ width: `${curso.porcentaje_completado}` }}
              >
                <div class="barra-progreso-1-3"></div>
                <div class="barra-progreso-circulo"></div>
              </div>
            </div>
            <div class="barra-progreso-texto">
              {curso.porcentaje_completado} completado
            </div>
          </div>
        </div>
        <div class="tarjeta-mis-cursos-info-2">
          <div class="tarjeta-mis-cursos-info-2-texto-1">
            {curso.nombre_curso}
          </div>
          <div class="tarjeta-mis-cursos-info-2-texto-2">
            {curso.nombre_profesor}
          </div>
          <div class="tarjeta-mis-cursos-info-2-horas-lecciones-idioma">
            <div class="horas">
              <i class="fi fi-rs-clock-five globo-2"></i>
              <div class="horas-texto">{curso.numero_horas}</div>
            </div>
            <div class="lecciones">
              <i class="fi fi-rs-book-alt globo-2"></i>
              <div class="horas-texto">
                {curso.progreso_lecciones} lecciones
              </div>
            </div>
            <div class="idioma-2">
              <i class="fi fi-rs-globe globo-2"></i>
              <div class="horas-texto">{curso.idioma}</div>
            </div>
          </div>
          <div class="tarjeta-mis-cursos-info-2-horas-lecciones-idioma">
            <div class="horas">
              <div class="vigencia-texto">
                Vigencia {curso.vigencia_dias} días
              </div>
            </div>
            <div class="horas-restantes">
              <i class="fi fi-rs-duration-alt globo-2"></i>
              <div class="horas-restantes-texto">{curso.horas_restantes}</div>
            </div>
            {/*
            <div class="frame-290011">
              {curso.certificado && <i class="fi fi-rs-diploma icon-4"></i>}
            </div>
            */}
          </div>
        </div>
      </div>
    ));
  };

  /**
   * Renderiza la sección seleccionada.
   * @returns {JSX.Element} - Elementos JSX que representan la sección seleccionada.
   */
  const mRenderSeccion = () => {
    switch (vSeccionSeleccionada) {
      case "Sin completar":
        return mRenderCursos(
          vCursos["cursos_por_estado"]?.["Sin completar"] || []
        );
      case "En progreso":
        return mRenderCursos(
          vCursos["cursos_por_estado"]?.["Sin completar"] || []
        );
      case "Por vencer":
        return mRenderCursos(vCursos["proximos_a_vencer"] || []);
      case "Completado":
        return mRenderCursos(
          vCursos["cursos_por_estado"]?.["Completado"] || []
        );
      default:
        return mRenderCursos(vCursosTodos);
    }
  };

  return (
    <div class="mis-cursos-info-2">
      <div class="info-2-cursos">{mRenderSeccion()}</div>
    </div>
  );
}
